import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./DNSManage.css";
import Navbar from "../../Common/Navbar";
import HostingDnsConnectPanel from "./HostingDnsConnectPanel";
import { connectRealtimeSocket } from "../../ServerPackage/CompleteCPanel/utils/realtimeSocket";

const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiSuffix = (base) => {
  const clean = normalizeBase(base);
  if (!clean) return "http://localhost:5000/api";
  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiSuffix(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

const DEFAULT_BACKEND_HOSTNAME = String(
  process.env.REACT_APP_BACKEND_HOSTNAME || "domainpaybackend.onrender.com"
)
  .trim()
  .replace(/^https?:\/\//, "")
  .replace(/\/+$/, "");

const DEFAULT_HOSTING_A_IP = String(
  process.env.REACT_APP_HOSTING_A_IP || "216.24.57.1"
).trim();

const DEFAULT_NAMESERVERS = [
  process.env.REACT_APP_PLATFORM_NS1 || "dns1.p09.nsone.net",
  process.env.REACT_APP_PLATFORM_NS2 || "dns2.p09.nsone.net",
  process.env.REACT_APP_PLATFORM_NS3 || "dns3.p09.nsone.net",
  process.env.REACT_APP_PLATFORM_NS4 || "dns4.p09.nsone.net",
]
  .map((item) => String(item || "").trim())
  .filter(Boolean);

const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_KEYS_TO_CLEAR = [
  ...AUTH_TOKEN_KEYS,
  "isLoggedIn",
  "user",
  "currentUser",
  "email",
  "userEmail",
  "user_email",
  "fullName",
  "name",
  "userName",
  "username",
];

const AUTO_VERIFY_INTERVAL_MS = 60 * 1000;
const VERIFY_COOLDOWN_SECONDS = 60;

const getStorageValue = (key) =>
  localStorage.getItem(key) || sessionStorage.getItem(key) || "";

const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const value = getStorageValue(key);
    if (value && String(value).trim()) return String(value).trim();
  }
  return "";
};

const clearAuth = () => {
  AUTH_KEYS_TO_CLEAR.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  window.dispatchEvent(
    new CustomEvent("auth-state-changed", {
      detail: { source: "DNSManage", action: "clear-auth" },
    })
  );
};

const safeJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const recordTypes = ["A", "AAAA", "CNAME", "MX", "TXT", "NS"];

const initialZoneForm = { domainName: "" };

const initialRecordForm = {
  recordType: "A",
  name: "@",
  value: "",
  ttl: "300",
  priority: "",
};

const cleanDomain = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/:\d+$/, "")
    .replace(/\/.*$/, "")
    .replace(/\.$/, "")
    .replace(/\s+/g, "");

const isValidDomain = (value = "") =>
  /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(cleanDomain(value));

const isValidIPv4 = (value = "") =>
  /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/.test(
    String(value || "").trim()
  );

const isValidTTL = (value = "") => {
  const ttl = Number(value);
  return Number.isInteger(ttl) && ttl >= 60 && ttl <= 86400;
};

const normalizeNameservers = (source = {}) => {
  const fromArray = Array.isArray(source.nameservers) ? source.nameservers : [];

  const fromKeys = [
    source.nameserver1,
    source.nameserver2,
    source.nameserver3,
    source.nameserver4,
    source.ns1,
    source.ns2,
    source.ns3,
    source.ns4,
  ];

  const finalNs = [...fromArray, ...fromKeys, ...DEFAULT_NAMESERVERS]
    .map((item) => String(item || "").trim())
    .filter(Boolean);

  return [...new Set(finalNs)];
};

const normalizeZone = (zone = {}) => ({
  id: zone.id,
  userId: zone.userId || zone.user_id,
  domainName: zone.domainName || zone.domain_name || zone.domain || "",
  status: zone.status || "active",
  type: zone.type || zone.zoneType || zone.zone_type || "primary",
  provider: "DoHostingPay DNS",
  nameservers: normalizeNameservers(zone),
});

const normalizeRecord = (record = {}) => ({
  id: record.id,
  userId: record.userId || record.user_id,
  zoneId: record.zoneId || record.zone_id,
  domainName: record.domainName || record.domain_name || "",
  recordType: record.recordType || record.record_type || "A",
  name: record.name || record.recordName || record.record_name || "@",
  value: record.value || record.recordValue || record.record_value || "",
  ttl: Number(record.ttl || 300),
  priority:
    record.priority === null || record.priority === undefined
      ? ""
      : record.priority,
  status: record.status || "active",
});

const normalizeDefaults = (defaults = {}) => ({
  provider: "DoHostingPay DNS",
  rootA: defaults.rootA || defaults.hostingAIp || DEFAULT_HOSTING_A_IP,
  wwwCname:
    defaults.wwwCname ||
    defaults.hostingCnameTarget ||
    defaults.backendHostname ||
    DEFAULT_BACKEND_HOSTNAME,
  nameservers: normalizeNameservers(defaults),
});

const getRealtimeLabel = (status) => {
  if (status === "connected") return "Realtime connected";
  if (status === "connecting") return "Connecting realtime...";
  return "Realtime offline";
};

const hasRecord = (records = [], type = "", name = "") => {
  const cleanType = String(type || "").trim().toUpperCase();
  const cleanName = String(name || "@").trim().toLowerCase();

  return records.some(
    (record) =>
      String(record.recordType || "").trim().toUpperCase() === cleanType &&
      String(record.name || "@").trim().toLowerCase() === cleanName
  );
};

const getDnsSetupStatus = (records = []) => {
  const hasRootA = hasRecord(records, "A", "@");
  const hasWwwCname = hasRecord(records, "CNAME", "www");

  if (hasRootA && hasWwwCname) {
    return {
      status: "ready",
      label: "Website DNS Connected",
      message:
        "Your website records are saved. The live domain is now connecting globally.",
    };
  }

  if (hasRootA || hasWwwCname) {
    return {
      status: "partial",
      label: "Website DNS Partially Connected",
      message:
        "One hosting record is missing. Add both root and www records to complete setup.",
    };
  }

  return {
    status: "pending",
    label: "Website DNS Setup Required",
    message:
      "Connect hosting DNS to make this domain point to your uploaded website.",
  };
};

function DNSManage() {
  const [search, setSearch] = useState("");
  const [zones, setZones] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");

  const [dnsDefaults, setDnsDefaults] = useState(
    normalizeDefaults({
      rootA: DEFAULT_HOSTING_A_IP,
      wwwCname: DEFAULT_BACKEND_HOSTNAME,
      nameservers: DEFAULT_NAMESERVERS,
    })
  );

  const [zoneForm, setZoneForm] = useState(initialZoneForm);
  const [recordForm, setRecordForm] = useState(initialRecordForm);

  const [showZoneForm, setShowZoneForm] = useState(false);
  const [showRecordForm, setShowRecordForm] = useState(false);

  const [loading, setLoading] = useState(true);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [savingZone, setSavingZone] = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);
  const [connectingHosting, setConnectingHosting] = useState(false);
  const [verifyingHosting, setVerifyingHosting] = useState(false);
  const [hostingVerifyResult, setHostingVerifyResult] = useState(null);
  const [deletingId, setDeletingId] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");
  const [verifyCooldown, setVerifyCooldown] = useState(0);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const hostingAIp = dnsDefaults.rootA || DEFAULT_HOSTING_A_IP;
  const backendHostname = dnsDefaults.wwwCname || DEFAULT_BACKEND_HOSTNAME;
  const platformNameservers =
    dnsDefaults.nameservers?.length > 0
      ? dnsDefaults.nameservers
      : DEFAULT_NAMESERVERS;

  const platformNs1 = platformNameservers[0] || DEFAULT_NAMESERVERS[0];
  const platformNs2 = platformNameservers[1] || DEFAULT_NAMESERVERS[1];
  const platformNs3 = platformNameservers[2] || DEFAULT_NAMESERVERS[2];
  const platformNs4 = platformNameservers[3] || DEFAULT_NAMESERVERS[3];

  const getRecordHelpText = useCallback(
    (type) => {
      if (type === "A") return "IPv4 address used to connect your root domain.";
      if (type === "AAAA") return "IPv6 address. Leave empty unless IPv6 is supported.";
      if (type === "CNAME") return `Target hostname, for example ${backendHostname}.`;
      if (type === "MX") return "Mail server hostname, for example mail.yourdomain.com.";
      if (type === "TXT") return "TXT value for SPF, DKIM, DMARC, or verification text.";
      if (type === "NS") return `Nameserver hostname, for example ${platformNs1}.`;
      return "DNS record value";
    },
    [backendHostname, platformNs1]
  );

  const getDefaultValueForType = useCallback(
    (type, domainName = "") => {
      const clean = cleanDomain(domainName);

      if (type === "A") return hostingAIp;
      if (type === "CNAME") return backendHostname;
      if (type === "MX") return clean ? `mail.${clean}` : "";
      if (type === "NS") return platformNs1;
      if (type === "TXT") return "";
      if (type === "AAAA") return "";

      return "";
    },
    [hostingAIp, backendHostname, platformNs1]
  );

  const getDefaultNameForType = (type) => {
    if (type === "CNAME") return "www";
    if (type === "MX") return "@";
    if (type === "TXT") return "@";
    if (type === "NS") return "@";
    return "@";
  };

  const authFetch = useCallback(async (endpoint, options = {}) => {
    const token = getToken();

    if (!token) {
      clearAuth();
      throw new Error("Session expired. Please login again.");
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

    const data = await safeJson(response);

    if (response.status === 401 || response.status === 403) {
      clearAuth();
      throw new Error("Session expired. Please login again.");
    }

    if (response.status === 429) {
      throw new Error(
        data?.message ||
          "DNS check is already running. Automatic verification will continue."
      );
    }

    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || "Request failed.");
    }

    return data;
  }, []);

  const loadZones = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(true);
        else setRefreshing(true);

        setError("");

        const data = await authFetch("/dns/zones");
        const nextZones = Array.isArray(data?.data)
          ? data.data.map(normalizeZone)
          : [];

        setZones(nextZones);

        if (!selectedDomain && nextZones.length > 0) {
          setSelectedDomain(nextZones[0].domainName);
        }
      } catch (err) {
        setError(err.message || "Unable to load DNS zones.");
        setZones([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authFetch, selectedDomain]
  );

  const loadRecords = useCallback(
    async (domainName, { silent = false } = {}) => {
      const clean = cleanDomain(domainName);

      if (!clean) {
        setRecords([]);
        return;
      }

      try {
        if (!silent) setRecordsLoading(true);
        setError("");

        const data = await authFetch(
          `/dns/zones/${encodeURIComponent(clean)}/records`
        );

        const nextRecords = Array.isArray(data?.data)
          ? data.data.map(normalizeRecord)
          : [];

        setRecords(nextRecords);

        if (data?.defaults) setDnsDefaults(normalizeDefaults(data.defaults));

        if (data?.zone) {
          const zone = normalizeZone(data.zone);
          if (zone.nameservers?.length) {
            setDnsDefaults((prev) =>
              normalizeDefaults({ ...prev, nameservers: zone.nameservers })
            );
          }
        }
      } catch (err) {
        setRecords([]);
        setError(err.message || "Unable to load DNS records.");
      } finally {
        setRecordsLoading(false);
      }
    },
    [authFetch]
  );

  const verifyHostingDNS = useCallback(
    async ({ automatic = false } = {}) => {
      const domainName = cleanDomain(selectedDomain);

      if (!isValidDomain(domainName)) {
        if (!automatic) setError("Please select a valid domain first.");
        return;
      }

      try {
        if (!automatic) {
          setVerifyingHosting(true);
          setVerifyCooldown(VERIFY_COOLDOWN_SECONDS);
        }

        setError("");

        const data = await authFetch(
          `/dns/zones/${encodeURIComponent(domainName)}/verify-hosting`,
          {
            method: "POST",
            body: JSON.stringify({ automatic }),
          }
        );

        if (data?.data?.expected) {
          setDnsDefaults(normalizeDefaults(data.data.expected));
        }

        const verified = Boolean(data?.verified);

        setHostingVerifyResult({
          verified,
          message:
            data?.message ||
            (verified
              ? "Your custom domain is live."
              : "Your domain is connecting globally. Automatic checks will continue."),
          status: data?.status || "",
          checks: data?.data?.checks || {},
        });

        if (verified) {
          setMessage("✅ Your custom domain is live now.");
        } else if (!automatic) {
          setMessage(
            "🌍 Your domain is connecting globally. Preview website is already live. Automatic verification will continue."
          );
        }

        await loadRecords(domainName, { silent: true });
        await loadZones({ silent: true });
      } catch (err) {
        const text = err.message || "Unable to verify hosting DNS.";

        if (!automatic) {
          setMessage(
            "🌍 Automatic DNS verification is running. Your preview website is already live."
          );
          setError(text);
        }
      } finally {
        if (!automatic) setVerifyingHosting(false);
      }
    },
    [authFetch, loadRecords, loadZones, selectedDomain]
  );

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  useEffect(() => {
    if (selectedDomain) {
      setHostingVerifyResult(null);
      loadRecords(selectedDomain);
    } else {
      setRecords([]);
    }
  }, [selectedDomain, loadRecords]);

  useEffect(() => {
    if (verifyCooldown <= 0) return undefined;

    const timer = setInterval(() => {
      setVerifyCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [verifyCooldown]);

  useEffect(() => {
    if (!selectedDomain) return undefined;

    const timer = setInterval(() => {
      verifyHostingDNS({ automatic: true });
    }, AUTO_VERIFY_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [selectedDomain, verifyHostingDNS]);

  useEffect(() => {
    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    let refreshTimer = null;

    const softRefresh = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        loadZones({ silent: true });
        if (selectedDomain) loadRecords(selectedDomain, { silent: true });
      }, 500);
    };

    const handleConnected = () => setRealtimeStatus("connected");
    const handleDisconnected = () => setRealtimeStatus("offline");

    const handleRefresh = (payload = {}) => {
      const payloadDomain = cleanDomain(payload.domainName || "");

      if (
        payloadDomain &&
        selectedDomain &&
        payloadDomain !== cleanDomain(selectedDomain)
      ) {
        return;
      }

      setRealtimeStatus("connected");

      if (payload?.verified) {
        setHostingVerifyResult({
          verified: true,
          message: "Your custom domain is live.",
          checks: payload.checks || {},
        });
        setMessage("✅ Your custom domain is live now.");
      }

      softRefresh();
    };

    const handleAuthError = () => {
      clearAuth();
      setRealtimeStatus("offline");
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);

    socket.on("dns:update", handleRefresh);
    socket.on("domain:status", handleRefresh);
    socket.on("domain:mapped", handleRefresh);
    socket.on("dashboard:update", handleRefresh);
    socket.on("notification:new", handleRefresh);
    socket.on("activity:new", handleRefresh);
    socket.on("hosting_panel:project_update", handleRefresh);

    socket.on("connect_error", handleAuthError);

    if (socket.connected) handleConnected();

    return () => {
      clearTimeout(refreshTimer);

      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);

      socket.off("dns:update", handleRefresh);
      socket.off("domain:status", handleRefresh);
      socket.off("domain:mapped", handleRefresh);
      socket.off("dashboard:update", handleRefresh);
      socket.off("notification:new", handleRefresh);
      socket.off("activity:new", handleRefresh);
      socket.off("hosting_panel:project_update", handleRefresh);

      socket.off("connect_error", handleAuthError);
    };
  }, [loadRecords, loadZones, selectedDomain]);

  const filteredZones = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return zones;

    return zones.filter((zone) =>
      String(zone.domainName || "").toLowerCase().includes(keyword)
    );
  }, [search, zones]);

  const selectedZone = useMemo(
    () =>
      zones.find(
        (zone) => cleanDomain(zone.domainName) === cleanDomain(selectedDomain)
      ) || null,
    [selectedDomain, zones]
  );

  const dnsSetup = useMemo(() => getDnsSetupStatus(records), [records]);

  const handleManualRefresh = () => {
    loadZones({ silent: true });
    if (selectedDomain) loadRecords(selectedDomain, { silent: true });
  };

  const openZoneModal = () => {
    setZoneForm(initialZoneForm);
    setShowZoneForm(true);
    setShowRecordForm(false);
    setError("");
    setMessage("");
  };

  const openRecordModal = (domainName) => {
    const clean = cleanDomain(domainName);

    setSelectedDomain(clean);
    setRecordForm({
      ...initialRecordForm,
      value: getDefaultValueForType(initialRecordForm.recordType, clean),
    });
    setShowRecordForm(true);
    setShowZoneForm(false);
    setError("");
    setMessage("");
  };

  const closeModals = () => {
    setShowZoneForm(false);
    setShowRecordForm(false);
    setZoneForm(initialZoneForm);
    setRecordForm(initialRecordForm);
  };

  const handleZoneChange = (e) => {
    const { name, value } = e.target;
    setZoneForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setMessage("");
  };

  const handleRecordChange = (e) => {
    const { name, value } = e.target;

    if (name === "recordType") {
      const nextType = String(value || "A").toUpperCase();

      setRecordForm((prev) => ({
        ...prev,
        recordType: nextType,
        name: getDefaultNameForType(nextType),
        value: getDefaultValueForType(nextType, selectedDomain),
        priority: nextType === "MX" ? prev.priority || "10" : "",
      }));

      setError("");
      setMessage("");
      return;
    }

    setRecordForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setMessage("");
  };

  const handleCreateZone = async (e) => {
    e.preventDefault();

    const domainName = cleanDomain(zoneForm.domainName);

    if (!isValidDomain(domainName)) {
      setError("Please enter a valid domain name, for example govroad.com.");
      return;
    }

    try {
      setSavingZone(true);
      setError("");
      setMessage("");

      const data = await authFetch("/dns/zones", {
        method: "POST",
        body: JSON.stringify({ domainName }),
      });

      const createdZone = normalizeZone(data?.data || {});

      setZones((prev) => {
        const exists = prev.some(
          (zone) =>
            cleanDomain(zone.domainName) === cleanDomain(createdZone.domainName)
        );

        if (exists) {
          return prev.map((zone) =>
            cleanDomain(zone.domainName) === cleanDomain(createdZone.domainName)
              ? createdZone
              : zone
          );
        }

        return [createdZone, ...prev];
      });

      setSelectedDomain(createdZone.domainName || domainName);
      setMessage("DNS zone created successfully.");
      closeModals();
      loadZones({ silent: true });
    } catch (err) {
      setError(err.message || "Unable to create DNS zone.");
    } finally {
      setSavingZone(false);
    }
  };

  const handleSaveRecord = async (e) => {
    e.preventDefault();

    const domainName = cleanDomain(selectedDomain);
    const recordType = String(recordForm.recordType || "").trim().toUpperCase();
    const name = String(recordForm.name || "@").trim() || "@";
    const value = String(recordForm.value || "").trim();
    const ttl = String(recordForm.ttl || "300").trim() || "300";
    const priority = String(recordForm.priority || "").trim();

    if (!isValidDomain(domainName)) {
      setError("Please select a valid domain first.");
      return;
    }

    if (!recordTypes.includes(recordType)) {
      setError("Please select a valid DNS record type.");
      return;
    }

    if (!value) {
      setError("DNS record value is required.");
      return;
    }

    if (recordType === "A" && !isValidIPv4(value)) {
      setError("A record value must be a valid IPv4 address.");
      return;
    }

    if (!isValidTTL(ttl)) {
      setError("TTL must be between 60 and 86400 seconds.");
      return;
    }

    try {
      setSavingRecord(true);
      setError("");
      setMessage("");

      const data = await authFetch(
        `/dns/zones/${encodeURIComponent(domainName)}/records`,
        {
          method: "POST",
          body: JSON.stringify({ recordType, name, value, ttl, priority }),
        }
      );

      const createdRecord = normalizeRecord(data?.data || {});

      setRecords((prev) => [createdRecord, ...prev]);
      setMessage("DNS record created successfully.");
      closeModals();
      loadRecords(domainName, { silent: true });
    } catch (err) {
      setError(err.message || "Unable to create DNS record.");
    } finally {
      setSavingRecord(false);
    }
  };

  const handleConnectHostingDNS = async () => {
    const domainName = cleanDomain(selectedDomain);

    if (!isValidDomain(domainName)) {
      setError("Please select a valid domain first.");
      return;
    }

    try {
      setConnectingHosting(true);
      setError("");
      setMessage("");
      setHostingVerifyResult(null);

      const data = await authFetch(
        `/dns/zones/${encodeURIComponent(domainName)}/connect-hosting`,
        {
          method: "POST",
          body: JSON.stringify({ ttl: 300 }),
        }
      );

      if (data?.setup) setDnsDefaults(normalizeDefaults(data.setup));

      const nextRecords = Array.isArray(data?.data)
        ? data.data.map(normalizeRecord)
        : [];

      if (nextRecords.length) {
        setRecords((prev) => {
          const merged = [...nextRecords, ...prev];
          const seen = new Set();

          return merged.filter((record) => {
            const key = `${record.recordType}:${record.name}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        });
      }

      setMessage(
        `✅ Website records saved for ${domainName}. Your preview website is live. Custom domain is connecting globally and will auto-verify.`
      );

      await loadRecords(domainName, { silent: true });
      verifyHostingDNS({ automatic: true });
    } catch (err) {
      setError(err.message || "Unable to connect hosting DNS.");
    } finally {
      setConnectingHosting(false);
    }
  };

  const handleVerifyHostingDNS = () => {
    if (verifyCooldown > 0) {
      setMessage(
        `Automatic verification is already running. Next check in ${verifyCooldown}s.`
      );
      return;
    }

    verifyHostingDNS({ automatic: false });
  };

  const handleDeleteRecord = async (recordId) => {
    if (!recordId) return;

    try {
      setDeletingId(String(recordId));
      setError("");
      setMessage("");

      await authFetch(`/dns/records/${recordId}`, { method: "DELETE" });

      setRecords((prev) =>
        prev.filter((record) => Number(record.id) !== Number(recordId))
      );

      setMessage("DNS record deleted successfully.");
    } catch (err) {
      setError(err.message || "Unable to delete DNS record.");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div>
      <Navbar />

      <div className="dns-page">
        <div className="dns-header">
          <span className="dns-eyebrow">Domain control panel</span>

          <h1>DNS Zones</h1>

          <p>
            Manage DNS records only for domains available in your own account.
            Your preview website stays live while the custom domain connects
            automatically in the background.
          </p>

          <div className="dns-status-row">
            <span
              className={
                realtimeStatus === "connected"
                  ? "dns-realtime online"
                  : "dns-realtime offline"
              }
            >
              {realtimeStatus === "connected" ? "🟢" : "⚪"}{" "}
              {getRealtimeLabel(realtimeStatus)}
            </span>

            <button
              type="button"
              className="dns-small-btn"
              onClick={handleManualRefresh}
              disabled={loading || refreshing}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {error ? <div className="dns-alert dns-error">{error}</div> : null}
        {message ? <div className="dns-alert dns-success">{message}</div> : null}

        <div className="dns-toolbar">
          <input
            type="text"
            placeholder="Search your domain..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <button type="button" className="dns-btn" onClick={openZoneModal}>
            + Add Domain
          </button>
        </div>

        <div className="dns-table">
          <div className="dns-table-header">
            <span>Domain</span>
            <span>Status</span>
            <span>Type</span>
            <span>Provider</span>
            <span>Action</span>
          </div>

          {loading ? (
            <div className="dns-empty-row">Loading DNS zones...</div>
          ) : filteredZones.length === 0 ? (
            <div className="dns-empty-row">
              No DNS zones found. Add your purchased domain.
            </div>
          ) : (
            filteredZones.map((zone) => (
              <div className="dns-row" key={zone.id || zone.domainName}>
                <span className="domain">{zone.domainName}</span>

                <span>
                  <span className={`badge ${String(zone.status).toLowerCase()}`}>
                    {zone.status}
                  </span>
                </span>

                <span>{zone.type}</span>
                <span>DoHostingPay DNS</span>

                <span>
                  <button
                    type="button"
                    className="view-btn"
                    onClick={() => {
                      const clean = cleanDomain(zone.domainName);
                      setSelectedDomain(clean);
                      loadRecords(clean, { silent: true });
                    }}
                  >
                    Manage
                  </button>
                </span>
              </div>
            ))
          )}
        </div>

        <div className="dns-records-card">
          <div className="dns-records-head">
            <div>
              <span className="dns-eyebrow">DNS Records</span>

              <h2>
                {selectedDomain
                  ? `${selectedDomain} records`
                  : "Select a domain"}
              </h2>
            </div>

            <div className="dns-record-actions">
              <button
                type="button"
                className="dns-btn"
                disabled={!selectedDomain}
                onClick={() => openRecordModal(selectedDomain)}
              >
                + Add Record
              </button>
            </div>
          </div>

          {selectedZone ? (
            <div className="dns-zone-summary">
              <span>Status: {selectedZone.status}</span>
              <span>Provider: DoHostingPay DNS</span>
              <span>Type: {selectedZone.type}</span>
              <span>Hosting: {dnsSetup.label}</span>
            </div>
          ) : null}

          <HostingDnsConnectPanel
            selectedDomain={selectedDomain}
            dnsSetup={dnsSetup}
            connectingHosting={connectingHosting}
            verifyingHosting={verifyingHosting}
            hostingVerifyResult={hostingVerifyResult}
            platformNs1={platformNs1}
            platformNs2={platformNs2}
            platformNs3={platformNs3}
            platformNs4={platformNs4}
            platformNameservers={platformNameservers}
            hostingAIp={hostingAIp}
            backendHostname={backendHostname}
            onConnectHosting={handleConnectHostingDNS}
            onVerifyHosting={handleVerifyHostingDNS}
            verifyCooldown={verifyCooldown}
          />

          {recordsLoading ? (
            <div className="dns-empty-row">Loading DNS records...</div>
          ) : !selectedDomain ? (
            <div className="dns-empty-row">
              Select or add a domain to manage DNS records.
            </div>
          ) : records.length === 0 ? (
            <div className="dns-empty-row">
              No DNS records found for {selectedDomain}. Click Connect Hosting
              DNS to create the required records.
            </div>
          ) : (
            <div className="dns-records-table">
              <div className="dns-records-header">
                <span>Type</span>
                <span>Name</span>
                <span>Value</span>
                <span>TTL</span>
                <span>Status</span>
                <span>Action</span>
              </div>

              {records.map((record) => (
                <div className="dns-record-row" key={record.id}>
                  <span>{record.recordType}</span>
                  <span>{record.name}</span>
                  <span className="dns-record-value">{record.value}</span>
                  <span>{record.ttl}</span>
                  <span>
                    <span
                      className={`badge ${String(record.status).toLowerCase()}`}
                    >
                      {record.status}
                    </span>
                  </span>
                  <span>
                    <button
                      type="button"
                      className="dns-delete-btn"
                      disabled={deletingId === String(record.id)}
                      onClick={() => handleDeleteRecord(record.id)}
                    >
                      {deletingId === String(record.id)
                        ? "Deleting..."
                        : "Delete"}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showZoneForm ? (
        <div className="dns-modal-overlay">
          <div className="dns-record-modal">
            <div className="dns-modal-top">
              <div>
                <span>Create DNS zone</span>
                <h2>Add your domain</h2>
              </div>

              <button type="button" className="dns-close-btn" onClick={closeModals}>
                ×
              </button>
            </div>

            <form onSubmit={handleCreateZone} className="dns-record-form">
              <label>
                Domain name
                <input
                  type="text"
                  name="domainName"
                  value={zoneForm.domainName}
                  onChange={handleZoneChange}
                  placeholder="govroad.com"
                />
                <small>Only domains available in your account can be added.</small>
              </label>

              <div className="dns-modal-actions">
                <button type="submit" className="dns-save-btn" disabled={savingZone}>
                  {savingZone ? "Saving..." : "Save Domain"}
                </button>

                <button type="button" className="dns-cancel-btn" onClick={closeModals}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showRecordForm ? (
        <div className="dns-modal-overlay">
          <div className="dns-record-modal">
            <div className="dns-modal-top">
              <div>
                <span>Create DNS record</span>
                <h2>
                  {selectedDomain
                    ? `Create record for ${selectedDomain}`
                    : "Create a new DNS record"}
                </h2>
              </div>

              <button type="button" className="dns-close-btn" onClick={closeModals}>
                ×
              </button>
            </div>

            <form onSubmit={handleSaveRecord} className="dns-record-form">
              <label>
                Record type
                <select
                  name="recordType"
                  value={recordForm.recordType}
                  onChange={handleRecordChange}
                >
                  {recordTypes.map((type) => (
                    <option value={type} key={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Name
                <input
                  type="text"
                  name="name"
                  value={recordForm.name}
                  onChange={handleRecordChange}
                  placeholder="@"
                />
                <small>
                  @ will automatically set {selectedDomain || "yourdomain.com"} as the host name.
                </small>
              </label>

              <label>
                Value
                <input
                  type="text"
                  name="value"
                  value={recordForm.value}
                  onChange={handleRecordChange}
                  placeholder={
                    recordForm.recordType === "A"
                      ? hostingAIp
                      : recordForm.recordType === "CNAME"
                      ? backendHostname
                      : "Record value"
                  }
                />
                <small>{getRecordHelpText(recordForm.recordType)}</small>
              </label>

              {recordForm.recordType === "MX" ? (
                <label>
                  Priority
                  <input
                    type="number"
                    name="priority"
                    value={recordForm.priority}
                    onChange={handleRecordChange}
                    placeholder="10"
                  />
                  <small>MX priority, lower value has higher priority.</small>
                </label>
              ) : null}

              <label>
                TTL in seconds optional
                <input
                  type="number"
                  name="ttl"
                  value={recordForm.ttl}
                  onChange={handleRecordChange}
                  placeholder="300"
                />
                <small>
                  Use 300 for faster DNS activation. Minimum allowed: 60 seconds.
                </small>
              </label>

              <div className="dns-modal-actions">
                <button
                  type="submit"
                  className="dns-save-btn"
                  disabled={savingRecord}
                >
                  {savingRecord ? "Saving..." : "Save Record"}
                </button>

                <button type="button" className="dns-cancel-btn" onClick={closeModals}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default DNSManage;